import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'

interface PillSliderProps {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  unit?: string
  displayMultiplier?: number
  displayPrecision?: number
  ticks?: number
  disabled?: boolean
  className?: string
  defaultValue?: number
  onReset?: () => void
  suffix?: React.ReactNode
}

export function PillSlider({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  unit = '%',
  displayMultiplier = 100,
  displayPrecision = 0,
  ticks = 8,
  disabled = false,
  className,
  defaultValue,
  onReset,
  suffix,
}: PillSliderProps) {
  const [localValue, setLocalValue] = useState(value)
  const isDragging = useRef(false)

  useEffect(() => {
    if (!isDragging.current) setLocalValue(value)
  }, [value])

  const raw = localValue * displayMultiplier
  const displayValue = displayPrecision > 0
    ? raw.toFixed(displayPrecision)
    : String(Math.round(raw))

  const isDefault = defaultValue != null && Math.abs(localValue - defaultValue) < step * 0.5

  return (
    <div className={cn(
      'relative flex items-center rounded-lg bg-neutral-100/80 h-[34px] px-3 gap-1.5',
      disabled && 'opacity-40 pointer-events-none',
      className,
    )}>
      <span className="text-[11px] font-medium text-neutral-500 shrink-0 select-none min-w-[5rem]">
        {label}
      </span>
      <div className="flex-1 relative flex items-center h-full">
        <div className="absolute inset-0 flex items-center pointer-events-none">
          {Array.from({ length: ticks + 1 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-px h-2.5 bg-neutral-300/40 rounded-full"
              style={{ left: `${(i / ticks) * 100}%` }}
            />
          ))}
        </div>
        {defaultValue != null && (
          <div
            className="absolute w-px h-3.5 bg-neutral-400/60 pointer-events-none z-[5]"
            style={{ left: `${((defaultValue - min) / (max - min)) * 100}%`, top: '50%', transform: 'translateY(-50%)' }}
          />
        )}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          disabled={disabled}
          aria-label={label}
          onPointerDown={() => { isDragging.current = true }}
          onPointerUp={() => { isDragging.current = false }}
          onChange={(e) => {
            const v = parseFloat(e.target.value)
            setLocalValue(v)
            onChange(v)
          }}
          onDoubleClick={() => {
            if (defaultValue != null && onReset) onReset()
          }}
          className={cn(
            'w-full relative z-10 appearance-none bg-transparent cursor-pointer h-4',
            '[&::-webkit-slider-runnable-track]:h-px [&::-webkit-slider-runnable-track]:bg-neutral-300/60 [&::-webkit-slider-runnable-track]:rounded-full',
            '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[5px] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-sm [&::-webkit-slider-thumb]:bg-neutral-500/80 [&::-webkit-slider-thumb]:-mt-[7.5px] [&::-webkit-slider-thumb]:shadow-sm',
            '[&::-moz-range-track]:h-px [&::-moz-range-track]:bg-neutral-300/60 [&::-moz-range-track]:border-none',
            '[&::-moz-range-thumb]:w-[5px] [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-sm [&::-moz-range-thumb]:bg-neutral-500/80 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:shadow-sm',
          )}
        />
      </div>
      <span className={cn(
        'text-[11px] shrink-0 tabular-nums select-none min-w-[2rem] text-right',
        isDefault ? 'text-neutral-300' : 'text-neutral-500',
      )}>
        {displayValue}{unit}
      </span>
      {onReset && !isDefault && (
        <button
          onClick={onReset}
          className="p-0.5 rounded text-neutral-300 hover:text-neutral-500 transition-colors shrink-0"
          aria-label={`Reset ${label}`}
        >
          <X size={10} />
        </button>
      )}
      {suffix}
    </div>
  )
}

interface PillToggleProps {
  label: string
  value: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  className?: string
}

export function PillToggle({
  label,
  value,
  onChange,
  disabled = false,
  className,
}: PillToggleProps) {
  return (
    <div className={cn(
      'flex items-center justify-between rounded-lg bg-neutral-100/80 h-[34px] px-3',
      disabled && 'opacity-40 pointer-events-none',
      className,
    )}>
      <span className="text-[11px] font-medium text-neutral-500 select-none">{label}</span>
      <button
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className={cn(
          'relative w-7 h-4 rounded-full transition-colors duration-150',
          value ? 'bg-blue-500' : 'bg-neutral-300',
        )}
      >
        <div className={cn(
          'absolute top-[2px] w-3 h-3 rounded-full bg-white shadow-sm transition-transform duration-150',
          value ? 'translate-x-[14px]' : 'translate-x-[2px]',
        )} />
      </button>
    </div>
  )
}
